import {
  AGE_BRACKETS,
  DEPARTMENTS,
  RADIUS_ESTIMATES,
  RADIUS_OPTIONS,
  RESIDENCE_OPTIONS,
  SECTORS,
  TOTAL_CITIZENS,
} from '../data/communications'
import type { AudiencePlace, AudienceState } from '../types/composer'
import type { Localized } from '../types/notice'

/** Citizens registered at a single civic address (bciti+ accounts). */
const CITIZENS_PER_ADDRESS = 3

/** Estimated citizens living in one place. */
export function getPlacePopulation(place: AudiencePlace): number {
  switch (place.kind) {
    case 'sector':
      return SECTORS.find((s) => s.id === place.sectorId)?.population ?? 0
    case 'address':
      return CITIZENS_PER_ADDRESS
    case 'radius':
      return RADIUS_ESTIMATES[place.radius] ?? 0
    case 'area':
      // Believable estimate that scales with the polygon complexity.
      return place.points.length >= 3
        ? Math.min(5000 + place.points.length * 3200, 42000)
        : 0
  }
}

/** Human label for one place, used in the list and in summaries. */
export function getPlaceLabel(place: AudiencePlace): Localized {
  switch (place.kind) {
    case 'sector': {
      const sector = SECTORS.find((s) => s.id === place.sectorId)
      return sector?.label ?? { fr: 'Secteur', en: 'District' }
    }
    case 'address':
      return { fr: place.label, en: place.label }
    case 'radius': {
      const r = RADIUS_OPTIONS.find((o) => o.value === place.radius)?.label
      return {
        fr: `${r?.fr ?? place.radius + ' m'} autour de ${place.label}`,
        en: `${r?.en ?? place.radius + ' m'} around ${place.label}`,
      }
    }
    case 'area':
      return {
        fr: `Zone dessinée (${place.points.length} points)`,
        en: `Drawn area (${place.points.length} points)`,
      }
  }
}

/** Citizens covered by WHERE — before the WHO filters are applied. */
export function getPlacesBase(audience: AudienceState): number {
  if (audience.scope === 'all') return TOTAL_CITIZENS
  const sum = audience.places.reduce((acc, p) => acc + getPlacePopulation(p), 0)
  return Math.min(sum, TOTAL_CITIZENS)
}

/** Share of citizens kept by the WHO filters (1 = no restriction). */
export function getFiltersFactor(audience: AudienceState): number {
  const { selectedDepartments, selectedAges, residence } = audience

  const departmentFactor =
    selectedDepartments.length > 0
      ? Math.min(
          DEPARTMENTS.filter((d) => selectedDepartments.includes(d.id)).reduce(
            (sum, d) => sum + d.subscribers,
            0,
          ) / TOTAL_CITIZENS,
          1,
        )
      : 1

  const ageFactor =
    selectedAges.length > 0
      ? AGE_BRACKETS.filter((a) => selectedAges.includes(a.id)).reduce(
          (sum, a) => sum + a.share,
          0,
        )
      : 1

  const residenceFactor =
    RESIDENCE_OPTIONS.find((r) => r.value === residence)?.share ?? 1

  return departmentFactor * ageFactor * residenceFactor
}

/** Number of WHO filters actually restricting the audience. */
export function countActiveFilters(audience: AudienceState): number {
  return (
    (audience.selectedDepartments.length > 0 ? 1 : 0) +
    (audience.selectedAges.length > 0 ? 1 : 0) +
    (audience.residence !== 'all' ? 1 : 0)
  )
}

/** Total number of citizens reached by the current audience selection. */
export function getAudienceTotal(audience: AudienceState): number {
  const base = getPlacesBase(audience)
  if (base === 0) return 0
  // A handful of fixed addresses is kept as-is: filtering 6 people by age
  // would round to zero and hide that the houses are targeted.
  const onlyAddresses =
    audience.scope === 'places' &&
    audience.places.every((p) => p.kind === 'address')
  if (onlyAddresses) return base
  return Math.max(1, Math.round(base * getFiltersFactor(audience)))
}

/** One-line description of the audience, for the review panel and ATNA. */
export function describeAudience(
  audience: AudienceState,
  lang: 'fr' | 'en',
): string {
  const where =
    audience.scope === 'all' || audience.places.length === 0
      ? lang === 'fr'
        ? 'Toute la ville'
        : 'Entire city'
      : summarizePlaces(audience.places, lang)
  const filters = countActiveFilters(audience)
  if (filters === 0) return where
  return lang === 'fr'
    ? `${where} · ${filters} filtre${filters > 1 ? 's' : ''}`
    : `${where} · ${filters} filter${filters > 1 ? 's' : ''}`
}

/** "2 secteurs + 3 adresses + 1 rayon" */
export function summarizePlaces(
  places: AudiencePlace[],
  lang: 'fr' | 'en',
): string {
  const count = (kind: AudiencePlace['kind']) =>
    places.filter((p) => p.kind === kind).length
  const parts: string[] = []
  const add = (n: number, fr: [string, string], en: [string, string]) => {
    if (n === 0) return
    const [one, many] = lang === 'fr' ? fr : en
    parts.push(`${n} ${n > 1 ? many : one}`)
  }
  add(count('sector'), ['secteur', 'secteurs'], ['district', 'districts'])
  add(count('address'), ['adresse', 'adresses'], ['address', 'addresses'])
  add(count('radius'), ['rayon', 'rayons'], ['radius', 'radii'])
  add(count('area'), ['zone dessinée', 'zones dessinées'], ['drawn area', 'drawn areas'])
  return parts.join(' + ')
}

/** Per-channel reach derived from the targeted audience. */
export function getReach(
  audienceTotal: number,
  basePercent: number,
  locale: string,
) {
  const count = Math.round(audienceTotal * (basePercent / 100))
  return {
    hasAudience: audienceTotal > 0,
    count: count.toLocaleString(locale),
    percent: basePercent,
  }
}
